/**
 * Confetti Animation
 * Lightweight confetti animation using Canvas API
 * Brand colors: Gold (#ad8330) and White (#ffffff)
 */

class ConfettiManager {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.particles = [];
    this.animationFrame = null;
    
    this.init();
    this.setupEventListeners();
  }

  init() {
    console.log('🎊 Initializing Confetti Manager...');
    
    // Create canvas element
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'confetti-canvas';
    this.canvas.style.position = 'fixed';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '999999'; // Extremely high z-index to be above cart drawer
    this.canvas.style.display = 'none';
    
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    
    console.log('  Canvas created and appended to body');
    
    // Set canvas size
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    console.log('  Canvas resized:', this.canvas.width, 'x', this.canvas.height);
  }

  setupEventListeners() {
    // Listen for confetti trigger event
    document.addEventListener('cart:confetti', (e) => {
      console.log('🎊 Confetti event received!', e.detail);
      // Colorful confetti: red, blue, green, yellow, purple, orange, pink
      const colors = e.detail?.colors || ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#6C5CE7'];
      this.trigger(colors);
    });
    
    console.log('✅ Confetti manager initialized and listening for cart:confetti events');
  }

  /**
   * Trigger confetti animation
   * @param {Array<string>} colors - Array of color hex codes
   */
  trigger(colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#6C5CE7']) {
    console.log('🎊 ===== CONFETTI TRIGGER START =====');
    console.log('  Canvas element:', this.canvas);
    console.log('  Canvas size:', this.canvas.width, 'x', this.canvas.height);
    console.log('  Colors:', colors);
    
    // Make sure canvas is visible
    this.canvas.style.display = 'block';
    console.log('  ✅ Canvas display set to block');
    
    // Clear any existing particles
    this.particles = [];
    
    // Create MORE confetti particles from MULTIPLE sources for better visibility
    const particleCount = 300; // Increased for more dramatic effect
    
    // Get cart drawer position if it exists, otherwise use full screen
    const cartDrawer = document.querySelector('.cart-drawer__dialog');
    let origins = [];
    
    if (cartDrawer && cartDrawer.open) {
      // Cart drawer is open - spawn confetti from top of drawer
      const rect = cartDrawer.getBoundingClientRect();
      console.log('  Cart drawer found! Rect:', rect);
      origins = [
        { x: rect.left + rect.width * 0.2, y: rect.top - 50 },
        { x: rect.left + rect.width * 0.4, y: rect.top - 50 },
        { x: rect.left + rect.width * 0.5, y: rect.top - 50 },
        { x: rect.left + rect.width * 0.6, y: rect.top - 50 },
        { x: rect.left + rect.width * 0.8, y: rect.top - 50 }
      ];
    } else {
      // Full screen confetti
      origins = [
        { x: window.innerWidth * 0.2, y: -50 },
        { x: window.innerWidth * 0.4, y: -50 },
        { x: window.innerWidth * 0.5, y: -50 },
        { x: window.innerWidth * 0.6, y: -50 },
        { x: window.innerWidth * 0.8, y: -50 }
      ];
    }
    
    console.log('  Creating', particleCount, 'particles from', origins.length, 'origins');
    
    origins.forEach(origin => {
      for (let i = 0; i < particleCount / origins.length; i++) {
        this.particles.push(this.createParticle(origin.x, origin.y, colors));
      }
    });
    
    console.log('  ✅ Total particles created:', this.particles.length);
    
    // Start animation
    this.animate();
    console.log('  ✅ Animation started');
    console.log('===== CONFETTI TRIGGER COMPLETE =====');
    
    // Auto-cleanup after 5 seconds (increased for better visibility)
    setTimeout(() => {
      console.log('🧹 Confetti auto-cleanup');
      this.stop();
    }, 5000);
  }

  /**
   * Create a single confetti particle
   * @param {number} x - Starting X position
   * @param {number} y - Starting Y position
   * @param {Array<string>} colors - Available colors
   * @returns {Object} Particle object
   */
  createParticle(x, y, colors) {
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 8 + 8; // Faster initial velocity
    
    return {
      x: x + (Math.random() - 0.5) * 100, // Spread particles horizontally
      y: y,
      vx: Math.cos(angle) * velocity * 0.3,
      vy: Math.random() * 5 + 3, // Downward velocity
      rotation: Math.random() * 360,
      rotationSpeed: Math.random() * 15 - 7.5, // Faster rotation
      size: Math.random() * 10 + 6, // Bigger particles
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: 1,
      gravity: 0.4, // Stronger gravity
      decay: 0.012, // Slower fade
      wind: (Math.random() - 0.5) * 0.5 // Side-to-side drift
    };
  }

  /**
   * Animation loop
   */
  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Update and draw each particle
    this.particles = this.particles.filter(particle => {
      // Update position
      particle.x += particle.vx + particle.wind;
      particle.y += particle.vy;
      particle.vy += particle.gravity;
      particle.vx *= 0.99; // Air resistance
      particle.rotation += particle.rotationSpeed;
      particle.opacity -= particle.decay;
      
      // Draw particle
      if (particle.opacity > 0) {
        this.ctx.save();
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate(particle.rotation * Math.PI / 180);
        this.ctx.globalAlpha = particle.opacity;
        this.ctx.fillStyle = particle.color;
        
        // Draw rectangle (confetti piece)
        const halfSize = particle.size / 2;
        this.ctx.fillRect(-halfSize, -halfSize, particle.size, particle.size * 0.6);
        
        this.ctx.restore();
        return true;
      }
      
      return false;
    });
    
    // Continue animation if particles remain
    if (this.particles.length > 0) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
    } else {
      console.log('🎊 All confetti particles faded');
      this.stop();
    }
  }

  /**
   * Stop animation and cleanup
   */
  stop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.particles = [];
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvas.style.display = 'none';
    console.log('🎊 Confetti stopped and cleaned up');
  }
}

// Initialize confetti manager
const confettiManager = new ConfettiManager();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.confettiManager = confettiManager;
  console.log('💡 window.confettiManager available for debugging');
}

export default confettiManager;
